/**
 * PI đang đi tới chứng từ nào — cho ô theo dõi trên trang KPI.
 *
 * Chỉ dùng MỐC CÓ THẬT trong sổ: đơn bán hàng, phiếu đóng gói, hóa đơn, phiếu
 * giao, tiền đã thu. Không bịa thêm bước nào khách chưa xác nhận (duyệt mẫu,
 * kiểm hàng...) — nhìn thấy một bước trên màn hình là người dùng tin hệ thống
 * đang theo dõi bước đó.
 */

/** Thứ tự đi tới của một PI. Chỉ số lớn hơn = đi xa hơn. */
export const PI_STAGES = ["created", "ordered", "packed", "invoiced", "delivered", "settled"] as const;
export type PiStage = (typeof PI_STAGES)[number];

export interface PiProgressFlags {
  /** Đơn bán hàng chưa hủy gắn PI này. */
  hasSalesOrder: boolean;
  hasPacking: boolean;
  hasInvoice: boolean;
  /** Có phiếu giao chưa hủy — hàng đã lên đường. */
  hasDelivery: boolean;
  /** Có phiếu giao ở trạng thái Delivered — khách đã nhận. */
  delivered: boolean;
  paidMinor: number;
  totalMinor: number;
}

/**
 * Mốc xa nhất PI đã đạt.
 *
 * KHÔNG đòi các mốc phải liên tiếp: thực tế nhiều PI xuất hóa đơn mà không hề
 * có đơn bán hàng hay phiếu đóng gói. Trả về mốc xa nhất, còn giao diện liệt
 * kê từng mốc để chỗ trống ở giữa vẫn nhìn thấy được.
 */
export function piStage(flags: PiProgressFlags): PiStage {
  // Thu đủ tiền là mốc cuối. Tổng bằng 0 hoặc chưa biết thì KHÔNG tính là đã
  // thu xong — nếu không, một PI trống rỗng sẽ hiện "đã tất toán".
  if (flags.totalMinor > 0 && flags.paidMinor >= flags.totalMinor) return "settled";
  if (flags.delivered) return "delivered";
  if (flags.hasInvoice) return "invoiced";
  if (flags.hasPacking) return "packed";
  // Phiếu giao đã lập nhưng chưa giao xong: vẫn tính là đã đóng gói xong việc
  // giấy tờ trước đó, không nhảy lên "đã giao".
  if (flags.hasDelivery) return "packed";
  if (flags.hasSalesOrder) return "ordered";
  return "created";
}

/** Đã thu tiền tới đâu — CHỈ trạng thái, không con số. */
export type PiSettlement = "unpaid" | "partial" | "paid";
export function piSettlement(paidMinor: number, totalMinor: number): PiSettlement {
  if (totalMinor > 0 && paidMinor >= totalMinor) return "paid";
  return paidMinor > 0 ? "partial" : "unpaid";
}

export interface PiProgressRow extends PiProgressFlags {
  piId: string;
  piNo: string;
  customerName: string | null;
  date: number | null;
  stage: PiStage;
  settlement: PiSettlement;
}
